import React, { useState, useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LaunchIcon from "@mui/icons-material/Launch";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from "@mui/material/Divider";
import { AppContext } from "../../context/AppContextProvider";
import { menu } from "../../layouts/menu";
import { useNavigate } from "react-router-dom";

const HaqtDrawer = (props) => {
  const { drawerWidth, drawerOpen, handleDrawerOpen, handleDrawerClose } =
    props;
  const { user, setUser } = useContext(AppContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));


  const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  }));

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(drawerOpen && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!drawerOpen && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  }));

  const MenuItem = ({ item }) => {
    const Component = hasChildren(item) ? MultiLevel : SingleLevel;
    return <Component item={item} />;
  };

  const SingleLevel = ({ item }) => {
    return (
      <ListItem
        onClick={() => {
          if (item.to === "adminportal") {
            //TODO: redirect to link based on env
            window.open(process.env.REACT_APP_ADMIN_API_URL, '_blank')
          } else {
            navigate(item.to);
          }
          isSmallScreen && handleDrawerClose();
        }}
        style={{
          cursor: "pointer",
          // marginLeft: '-12px',
          marginLeft: item.level === "child" ? "8px" : "-12px",
          paddingRight: 0,
        }}
      >
        <ListItemIcon
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div>{item.icon}</div>
          {!drawerOpen && (
            <Box sx={{ fontSize: "10px", fontWeight: "normal", color: "#000" }}>
              {item.title}
            </Box>
          )}
        </ListItemIcon>
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            style: {
              fontWeight: "bold",
              fontSize: item.level === "child" ? "13px" : "15px",
              // color: "#005572",
              paddingLeft: "8px",
              color: item.color ? item.color : "#005572",
            },
          }}
        />
        {drawerOpen && item.type === "link" && (
          <LaunchIcon style={{ color: item.color }} />
        )}
      </ListItem>
    );
  };

  const MultiLevel = ({ item }) => {
    const { items: children } = item;
    const filteredChildrenBasedOnRole = filterMenuItemsByRole(children);
    const [menuOpen, setMenuOpen] = useState(false);

    const handleClick = () => {
      !drawerOpen && handleDrawerOpen();
      setMenuOpen((prev) => !prev);
    };

    return (
      <React.Fragment>
        <ListItem
          title={!drawerOpen ? item.title : ""}
          onClick={handleClick}
          style={{
            cursor: "pointer",
            marginLeft: "-12px",
            paddingRight: 0,
          }}
        >
          <ListItemIcon
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div>{item.icon}</div>
            {!drawerOpen && (
              <Box
                sx={{ fontSize: "10px", fontWeight: "normal", color: "#000" }}
              >
                {item.title}
              </Box>
            )}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{
              style: {
                fontWeight: "bold",
                fontSize: item.level === "child" ? "13px" : "15px",
                paddingLeft: "8px",
                color: item.color ? item.color : "#005572",
              },
            }}
          />
          {menuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
        <Collapse in={menuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {filteredChildrenBasedOnRole.map((child, key) => (
              <MenuItem key={key} item={child} />
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const hasChildren = (item) => {
    const { items: children } = item;

    if (children === undefined) {
      return false;
    }

    if (children.constructor !== Array) {
      return false;
    }

    if (children.length === 0) {
      return false;
    }

    return true;
  };

  const filterMenuItemsByRole = (menu) => {
    return menu.filter(item => {
      // If the menu has a role specified and the user's role matches, or if the item doesn't have a role specified
      return !item.role || (item.role && item.role === user.role);
    });
  };


  const filteredMenuItems = filterMenuItemsByRole(menu);

  return (
    <Drawer variant="permanent" open={drawerOpen} 
        sx={{

      '.MuiDrawer-paper': {
          width: (isSmallScreen && drawerOpen) ? '100%' :'inherit',
      },
    }}
    
    >
      <DrawerHeader sx={{ mt: 1 }} />
      <Divider />
      <div sx={{ flexGrow: 1 }}>
        {filteredMenuItems.map((item, key) => (
          <>
            <MenuItem key={key} item={item} />
            <Divider
              component="li"
              style={{ listStyleType: "none", margin: "2px" }}
            />
          </>
        ))}
      </div>
    </Drawer>
  );
};

export default HaqtDrawer;
